import * as React from 'react'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export interface ComboboxOption {
  label: string | React.ReactNode
  value: string
}

interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string) => void
  onSearch?: (search: string, signal: AbortSignal) => void
  placeholder?: string
  emptyText?: string
  searchPlaceholder?: string
  isLoading?: boolean
  disabled?: boolean
  className?: string
  isInvalid?: boolean
  renderOption?: (option: ComboboxOption) => React.ReactNode
  minSearchLength?: number
  shouldFilter?: boolean
}

export function Combobox({
  options,
  value,
  onValueChange,
  onSearch,
  placeholder = 'Select option...',
  emptyText = 'No results found.',
  searchPlaceholder = 'Search...',
  isLoading = false,
  disabled = false,
  className,
  isInvalid = false,
  renderOption,
  minSearchLength = 0,
  shouldFilter,
}: ComboboxProps) {
  const effectiveShouldFilter = shouldFilter ?? !onSearch
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState('')
  const searchTimeoutRef = React.useRef<NodeJS.Timeout>()
  const abortControllerRef = React.useRef<AbortController>()

  const selectedOption = React.useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  )

  const handleSearchChange = (search: string) => {
    setSearchValue(search)

    // Abort previous in-flight request
    abortControllerRef.current?.abort()

    if (onSearch && search.length >= minSearchLength) {
      // Debounce the search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      searchTimeoutRef.current = setTimeout(() => {
        const controller = new AbortController()
        abortControllerRef.current = controller
        onSearch(search, controller.signal)
      }, 500)
    }
  }

  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      abortControllerRef.current?.abort()
    }
  }, [])

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="tertiary"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between !bg-monochrome-3-60 !text-monochrome-1 hover:!bg-monochrome-3-60/90 border !border-monochrome-120',
            isInvalid && '!border-accent-3',
            !selectedOption && 'text-monochrome-120',
            className,
          )}
        >
          <span className="truncate">
            {selectedOption?.label || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-4 bg-monochrome-3-60-solid border-monochrome-120 rounded-2xl"
        align="center"
      >
        <Command
          className="bg-monochrome-3-60-solid"
          shouldFilter={effectiveShouldFilter}
        >
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={handleSearchChange}
            className="bg-monochrome-4 !text-monochrome-1"
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-monochrome-120" />
                <span className="ml-2 text-sm text-monochrome-120">
                  Loading...
                </span>
              </div>
            ) : (
              <>
                <CommandEmpty className="text-monochrome-120">
                  {emptyText}
                </CommandEmpty>
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={(currentValue) => {
                        onValueChange?.(
                          currentValue === value ? '' : currentValue,
                        )
                        setOpen(false)
                        setSearchValue('')
                      }}
                      className="text-monochrome-1 hover:bg-accent-1/10"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === option.value ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      {renderOption ? renderOption(option) : option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
